// src/components/ContactPanel.js
import { useState, useEffect, useMemo } from "react";
import {
  Avatar,
  StageBadge,
  HealthBadge,
  ActivityItem,
  ProgressBar,
  TagList,
  timeAgo,
  fmtDate,
  InvoicedDisplay,
} from "./UI";
import {
  watchTasksForContact,
  watchContact,
  watchScansCollection,
  transitionStage,
  updateContact,
  createContact,
  logActivity,
  createTask,
} from "../lib/db";
import { stripeCreatePaymentLinkCallable, stripeCreateRefundCallable } from "../lib/crmApi";
import { ensureCallableAuthContext } from "../lib/crmCallableAuth";
import { watchUserMergedTimeline } from "../lib/platformActivity";
import {
  watchContactFiles,
  watchContactWorkspaceUploadSummary,
  WORKSPACE_SUMMARY_MAX_FILES_PER_FIELD,
} from "../lib/userWorkspaceSummary";
import { formatSqft } from "../lib/scanSqft";
import { LIFECYCLE_STAGES, INDUSTRY_OPTIONS, INDUSTRY_MAP } from "../lib/constants";
import {
  formatWorkRole,
  getContactProfileDetailRows,
  hasContactProfileData,
} from "../lib/contactProfileFields";
import { getContactDeviceDetailRows, hasDeviceProfileData } from "../lib/deviceProfile";
import {
  buildScanRollupByUserRef,
  enrichContactWithScanRollup,
} from "../lib/contactScanRollup";
import ContactScansTab from "./ContactScansTab";

const TABS = ["Overview", "Scans", "Timeline", "Tasks", "Notes", "Stripe", "Firebase"];

function draftShapeFromContactRow(row) {
  return {
    name: row.name || "",
    email: row.email || "",
    phone: row.phone || "",
    companyName: row.companyName || "",
    role: row.role || "",
    stage: row.stage || "downloaded",
    industry: row.industry || "",
    stripeCustomerId: row.stripeCustomerId || "",
    firebaseUid: row.firebaseUid || "",
    mrr: row.mrr ?? 0,
    lifetimeRevenue: row.lifetimeRevenue != null && row.lifetimeRevenue !== "" ? String(row.lifetimeRevenue) : "",
    isInternal: row.isInternal === true,
    healthScoreManual: row.healthScoreManual === true,
    healthScoreEd: String(row.healthScore ?? 50),
    totalScans: row.totalScans ?? 0,
    notes: row.notes || "",
    customFieldRows: Object.entries(row.customFields || {}).map(([key, value]) => ({
      key,
      value: value == null ? "" : String(value),
    })),
  };
}

export default function ContactPanel({ contact, open, onClose, onContactCreated }) {
  const [activeTab, setActiveTab] = useState("Overview");
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [liveContact, setLiveContact] = useState(null);
  const [stageSaving, setStageSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [touchNote, setTouchNote] = useState("");
  const [touchSaving, setTouchSaving] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [plAmount, setPlAmount] = useState("");
  const [plName, setPlName] = useState("Scanbrix payment");
  const [plPriceId, setPlPriceId] = useState("");
  const [plUrl, setPlUrl] = useState("");
  const [plErr, setPlErr] = useState("");
  const [plBusy, setPlBusy] = useState(false);
  const [refundPi, setRefundPi] = useState("");
  const [refundAmt, setRefundAmt] = useState("");
  const [refundErr, setRefundErr] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  /** `undefined` = loading; object = loaded (may be empty arrays). */
  const [wsSummary, setWsSummary] = useState(undefined);
  const [scans, setScans] = useState([]);
  const [contactFiles, setContactFiles] = useState([]);
  const isNew = contact?.isNew === true;
  const productUserId = useMemo(() => {
    if (!contact) return "";
    const row = liveContact || contact;
    return String(row.firebaseUid || contact.firebaseUid || contact.id || "").trim();
  }, [contact, liveContact]);

  useEffect(() => {
    if (!open) return undefined;
    return watchScansCollection(setScans, () => {});
  }, [open]);

  useEffect(() => {
    if (!open || !productUserId) {
      setContactFiles([]);
      return undefined;
    }
    return watchContactFiles(productUserId, setContactFiles, () => {});
  }, [open, productUserId]);

  useEffect(() => {
    if (!open || !contact?.id || isNew) {
      setLiveContact(null);
      return;
    }
    return watchContact(contact.id, setLiveContact);
  }, [open, contact?.id, isNew]);

  useEffect(() => {
    if (!contact?.id || !open) {
      setActivity([]);
      setTasks([]);
      return;
    }
    if (!productUserId) {
      setActivity([]);
      setTasks([]);
      return;
    }
    const unsubA = watchUserMergedTimeline(productUserId, setActivity);
    const unsubT = watchTasksForContact(contact.id, setTasks);
    return () => {
      unsubA();
      unsubT();
    };
  }, [contact?.id, open, productUserId]);

  useEffect(() => {
    if (!open || !contact?.id || activeTab !== "Timeline") {
      setWsSummary(undefined);
      return undefined;
    }
    if (!productUserId) {
      setWsSummary({
        workspaces: [],
        totalFileDocs: 0,
        filesWithoutWorkspace: 0,
      });
      return undefined;
    }
    setWsSummary(undefined);
    return watchContactWorkspaceUploadSummary(productUserId, setWsSummary, () => {});
  }, [open, contact?.id, activeTab, productUserId]);

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setDraft(null);
      setSaveErr(null);
      setActiveTab("Overview");
      setCreating(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !contact?.isNew) return;
    setEditing(true);
    setDraft(draftShapeFromContactRow(contact));
    setSaveErr(null);
    setActiveTab("Overview");
  }, [open, contact?.isNew]);

  const c = useMemo(() => {
    const base = liveContact || contact;
    if (!base) return null;
    return enrichContactWithScanRollup(
      base,
      buildScanRollupByUserRef(scans),
      buildScanRollupByUserRef(contactFiles)
    );
  }, [liveContact, contact, scans, contactFiles]);

  if (!contact || !c) return null;
  const panelTabs = isNew ? ["Overview"] : TABS;

  function startEdit() {
    setDraft(draftShapeFromContactRow(c));
    setSaveErr(null);
    setEditing(true);
  }

  function pickStage(stageId) {
    if (isNew) {
      if (!draft) return;
      if (stageId === draft.stage) return;
      setDraft((d) => ({ ...d, stage: stageId }));
      return;
    }
    onPickStage(stageId);
  }

  async function saveEdit() {
    if (!draft) return;
    setSaveErr(null);
    const cleanedCf = {};
    (draft.customFieldRows || []).forEach((row) => {
      const k = String(row.key || "").trim();
      if (!k) return;
      cleanedCf[k] = row.value;
    });
    const fields = {
      name: draft.name.trim(),
      email: draft.email.trim(),
      phone: draft.phone.trim() || null,
      companyName: draft.companyName.trim(),
      role: draft.role.trim(),
      industry: draft.industry || null,
      stripeCustomerId: draft.stripeCustomerId.trim() || null,
      firebaseUid: draft.firebaseUid.trim() || null,
      mrr: Number(draft.mrr) || 0,
      lifetimeRevenue:
        draft.lifetimeRevenue !== "" && draft.lifetimeRevenue != null
          ? (() => {
              const n = Number(draft.lifetimeRevenue);
              return Number.isFinite(n) ? n : null;
            })()
          : null,
      isInternal: draft.isInternal === true,
      ...(draft.healthScoreManual
        ? {
            healthScoreManual: true,
            healthScore: Math.min(100, Math.max(0, Number(draft.healthScoreEd) || 0)),
          }
        : { healthScoreManual: false }),
      totalScans: Math.max(0, Number(draft.totalScans) || 0),
      notes: draft.notes,
      customFields: cleanedCf,
    };
    try {
      if (isNew) {
        if (!fields.name || !fields.email) {
          setSaveErr("Name and email are required.");
          return;
        }
        setCreating(true);
        const id = await createContact({
          ...fields,
          stage: draft.stage || "lead",
        });
        setEditing(false);
        setDraft(null);
        onContactCreated?.(id);
      } else {
        await updateContact(c.id, fields);
        setEditing(false);
        setDraft(null);
      }
    } catch (e) {
      setSaveErr(e?.message || "Save failed");
    } finally {
      if (isNew) setCreating(false);
    }
  }

  async function onPickStage(stageId) {
    if (stageId === c.stage || stageSaving) return;
    setStageSaving(true);
    try {
      await transitionStage(c.id, stageId, "manual");
    } catch (e) {
      console.error(e);
    } finally {
      setStageSaving(false);
    }
  }

  function saveNote() {
    if (!note.trim()) return;
    setNotes((prev) => [
      { id: Date.now(), text: note, author: "Jordan Davis", createdAt: new Date() },
      ...prev,
    ]);
    setNote("");
  }

  async function logTouchpoint() {
    if (!touchNote.trim() || touchSaving) return;
    setTouchSaving(true);
    try {
      await logActivity(c.id, "correspondence", touchNote.trim(), { source: "crm_manual" });
      setTouchNote("");
    } catch (e) {
      console.error(e);
    } finally {
      setTouchSaving(false);
    }
  }

  async function addTask() {
    const t = newTaskTitle.trim();
    if (!t) return;
    try {
      await createTask({ title: t, contactId: c.id, priority: "medium" });
      setNewTaskTitle("");
    } catch (e) {
      console.error(e);
    }
  }

  async function createPaymentLink() {
    setPlErr("");
    setPlBusy(true);
    setPlUrl("");
    try {
      const authCtx = await ensureCallableAuthContext();
      if (!authCtx.ok) throw new Error(authCtx.message);
      const fn = stripeCreatePaymentLinkCallable();
      const payload = {
        productName: plName.trim() || "Payment",
        currency: "usd",
        metadata: { crmContactId: c.id, crmEmail: c.email || "" },
      };
      if (plPriceId.trim()) {
        payload.priceId = plPriceId.trim();
      } else {
        const dollars = Number(plAmount);
        if (!dollars || dollars <= 0) throw new Error("Enter amount (USD) or a Stripe Price ID");
        payload.amount = Math.round(dollars * 100);
      }
      const { data } = await fn(payload);
      setPlUrl(data.url);
      await logActivity(c.id, "stripe_payment_link", `Payment link created`, { url: data.url, linkId: data.id });
    } catch (e) {
      setPlErr(e?.message || String(e));
    } finally {
      setPlBusy(false);
    }
  }

  async function runRefund() {
    const pi = refundPi.trim();
    if (!pi) {
      setRefundErr("Payment Intent ID required");
      return;
    }
    setRefundErr("");
    setRefundBusy(true);
    try {
      const authCtx = await ensureCallableAuthContext();
      if (!authCtx.ok) throw new Error(authCtx.message);
      const fn = stripeCreateRefundCallable();
      const amt = refundAmt.trim() ? Math.round(Number(refundAmt) * 100) : undefined;
      const isPi = pi.startsWith("pi_");
      const isCh = pi.startsWith("ch_");
      if (!isPi && !isCh) throw new Error("Enter a Payment Intent ID (pi_…) or Charge ID (ch_…)");
      const { data } = await fn({
        paymentIntentId: isPi ? pi : undefined,
        chargeId: isCh ? pi : undefined,
        amountCents: amt,
      });
      await logActivity(c.id, "stripe_refund", `Refund ${data.status}: ${data.id}`, { refundId: data.id });
      setRefundPi("");
      setRefundAmt("");
    } catch (e) {
      setRefundErr(e?.message || String(e));
    } finally {
      setRefundBusy(false);
    }
  }

  const headerIndustry = isNew && draft && draft.industry ? draft.industry : c.industry;
  const industryLabel = headerIndustry ? INDUSTRY_MAP[headerIndustry] || headerIndustry : null;
  const professionLabel =
    formatWorkRole(c.workRole, c.workRoleOther) ||
    industryLabel ||
    (isNew && draft ? draft.role : c.role) ||
    null;
  const profileDetails = getContactProfileDetailRows(c);
  const deviceDetails = getContactDeviceDetailRows(c);
  const showProfileSections = !isNew && hasContactProfileData(c);
  const showDeviceSection = !isNew && hasDeviceProfileData(c);
  const displayStage = isNew && draft ? draft.stage : (c.displayStage ?? c.stage);
  const displayName =
    (isNew && draft?.name?.trim()) || (!isNew && c.name?.trim())
      ? isNew && draft
        ? draft.name
        : c.name
      : isNew
        ? "New contact"
        : c.name || "—";

  return (
    <div className={`slide-panel ${open ? "open" : ""}`}>
      <div className="panel-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 16 }}>
          <Avatar name={displayName} size="lg" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 2 }}>{displayName}</div>
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>
              {professionLabel}
              {(isNew && draft ? draft.companyName : c.companyName)
                ? ` · ${isNew && draft ? draft.companyName : c.companyName}`
                : ""}
            </div>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <StageBadge stage={displayStage} />
              {industryLabel && industryLabel !== professionLabel ? (
                <span className="badge badge-muted" style={{ fontSize: 11 }}>
                  {industryLabel}
                </span>
              ) : null}
              {c.location ? (
                <span className="badge badge-muted" style={{ fontSize: 11 }}>
                  {c.location}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{ alignSelf: "flex-start", fontSize: 18, lineHeight: 1 }}
          >
            ×
          </button>
        </div>
        <div className="panel-tabs panel-tabs--scroll">
          {panelTabs.map((t) => (
            <div
              key={t}
              className={`panel-tab ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
              role="tab"
            >
              {t}
            </div>
          ))}
        </div>
      </div>

      <div className="panel-body">
        {activeTab === "Overview" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 12,
              }}
            >
              {isNew ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" className="btn btn-sm" onClick={onClose}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" disabled={creating} onClick={saveEdit}>
                    {creating ? "Creating…" : "Create contact"}
                  </button>
                </div>
              ) : !editing ? (
                <button type="button" className="btn btn-sm btn-primary" onClick={startEdit}>
                  Edit contact
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" className="btn btn-sm" onClick={() => { setEditing(false); setDraft(null); }}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={saveEdit}>
                    Save
                  </button>
                </div>
              )}
            </div>
            {saveErr ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: 10,
                  borderRadius: 8,
                  background: "var(--red-dim)",
                  color: "var(--red)",
                  fontSize: 13,
                }}
              >
                {saveErr}
              </div>
            ) : null}

            <div
              className="contact-overview-stats"
              style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 20 }}
            >
              {[
                {
                  label: "Invoiced",
                  value:
                    c.lifetimeRevenue != null && Number(c.lifetimeRevenue) > 0
                      ? `$${Number(c.lifetimeRevenue).toLocaleString()}`
                      : c.mrr
                        ? `$${Number(c.mrr).toLocaleString()}`
                        : "—",
                  color:
                    (c.lifetimeRevenue != null && Number(c.lifetimeRevenue) > 0) || c.mrr
                      ? "var(--green)"
                      : "var(--text-3)",
                },
                {
                  label: c.healthScoreManual ? "Health (locked)" : "Health (live)",
                  value: c.healthScore ?? "—",
                  color: c.healthScore >= 70 ? "var(--green)" : c.healthScore >= 40 ? "var(--amber)" : "var(--red)",
                },
                {
                  label: c.leadScoreManual ? "Lead (locked)" : "Lead (live)",
                  value: c.leadScore ?? "—",
                  color: c.leadScore >= 70 ? "var(--purple)" : c.leadScore >= 40 ? "var(--accent)" : "var(--text-2)",
                },
                { label: "Scans", value: c.totalScans ?? 0, color: "var(--text-1)" },
                ...(c.lastScanAt
                  ? [{ label: "Last scan", value: timeAgo(c.lastScanAt), color: "var(--purple)" }]
                  : []),
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    background: "var(--bg-elevated)",
                    border: "0.5px solid var(--border)",
                    borderRadius: 9,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      color: "var(--text-3)",
                      marginBottom: 5,
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.5px", color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>

            {!isNew ? (
              <div style={{ marginBottom: 18 }}>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    color: "var(--text-3)",
                    marginBottom: 8,
                  }}
                >
                  Acquisition
                </div>
                {(() => {
                  const acq = c.acquisition || {};
                  const level = acq.evidence_level || "unknown";
                  const rows = [
                    ["Evidence", level],
                    ["Content", acq.content_id || "—"],
                    ["CTA", acq.cta_placement || "—"],
                    ["Handoff", acq.handoff_method || "—"],
                    ["Apple campaign", acq.apple_campaign || "—"],
                    ["Referrer", acq.referrer_host || acq.first_touch?.referrer_host || "—"],
                    [
                      "First touch",
                      acq.first_touch
                        ? [acq.first_touch.source, acq.first_touch.medium, acq.first_touch.campaign]
                            .filter(Boolean)
                            .join(" / ") || "—"
                        : "—",
                    ],
                  ];
                  return rows.map(([k, v]) => (
                    <div key={k} className="detail-row">
                      <div className="detail-key">{k}</div>
                      <div className="detail-val">
                        {k === "Evidence" ? (
                          <span className="badge badge-muted" style={{ fontSize: 11 }}>
                            {v}
                          </span>
                        ) : (
                          v
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            ) : null}

            {!editing ? (
              <div style={{ marginBottom: 18 }}>
                {showProfileSections ? (
                  <>
                    {profileDetails.onboarding.length > 0 ? (
                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            color: "var(--text-3)",
                            marginBottom: 8,
                          }}
                        >
                          Onboarding
                        </div>
                        {profileDetails.onboarding.map((row) => (
                          <div key={row.label} className="detail-row">
                            <div className="detail-key">{row.label}</div>
                            <div className="detail-val">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {profileDetails.profile.length > 0 ? (
                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            color: "var(--text-3)",
                            marginBottom: 8,
                          }}
                        >
                          Profile
                        </div>
                        {profileDetails.profile.map((row) => (
                          <div key={row.label} className="detail-row">
                            <div className="detail-key">{row.label}</div>
                            <div className="detail-val">
                              {row.href ? (
                                <a href={row.href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
                                  {row.value}
                                </a>
                              ) : (
                                row.value
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {showDeviceSection && deviceDetails.length > 0 ? (
                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            color: "var(--text-3)",
                            marginBottom: 8,
                          }}
                        >
                          Device
                        </div>
                        {deviceDetails.map((row) => (
                          <div key={row.label} className="detail-row">
                            <div className="detail-key">{row.label}</div>
                            <div className="detail-val">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
                {[
                  ["Email", <a href={`mailto:${c.email}`} style={{ color: "var(--accent)" }}>{c.email}</a>],
                  ["Phone", c.phone || "—"],
                  ["Company", c.companyName || "—"],
                  ["Role", c.role || formatWorkRole(c.workRole, c.workRoleOther) || "—"],
                  ["Industry", industryLabel || "—"],
                  ["Last active", c.lastActiveAt ? timeAgo(c.lastActiveAt) : "—"],
                  ["Created", fmtDate(c.createdAt)],
                  ...(c.isInternal
                    ? [["Internal", <span className="badge badge-muted">Excluded from revenue metrics</span>]]
                    : []),
                  ...(c.firebaseUid
                    ? [["Firebase UID", <span className="mono" style={{ color: "var(--amber)" }}>{c.firebaseUid}</span>]]
                    : []),
                  ...(c.stripeCustomerId
                    ? [["Stripe ID", <span className="mono" style={{ color: "var(--purple)" }}>{c.stripeCustomerId}</span>]]
                    : []),
                ].map(([k, v]) => (
                  <div key={k} className="detail-row">
                    <div className="detail-key">{k}</div>
                    <div className="detail-val">{v}</div>
                  </div>
                ))}
                {Object.keys(c.customFields || {}).length > 0 ? (
                  <div style={{ marginTop: 14 }}>
                    <div
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                        color: "var(--text-3)",
                        marginBottom: 8,
                      }}
                    >
                      Custom fields
                    </div>
                    {Object.entries(c.customFields).map(([k, v]) => (
                      <div key={k} className="detail-row">
                        <div className="detail-key">{k}</div>
                        <div className="detail-val">{String(v)}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : draft ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
                {[
                  ["Name", "name", "text"],
                  ["Email", "email", "email"],
                  ["Phone", "phone", "tel"],
                  ["Company", "companyName", "text"],
                  ["Role", "role", "text"],
                  ["Stripe customer ID", "stripeCustomerId", "text"],
                  ["Firebase UID", "firebaseUid", "text"],
                ].map(([label, field, type]) => (
                  <label key={field} style={{ display: "block", fontSize: 12, color: "var(--text-2)" }}>
                    {label}
                    <input
                      className="input"
                      type={type}
                      value={draft[field]}
                      onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                      style={{ marginTop: 4, width: "100%" }}
                    />
                  </label>
                ))}
                <label style={{ display: "block", fontSize: 12, color: "var(--text-2)" }}>
                  Industry
                  <select
                    className="input"
                    value={draft.industry}
                    onChange={(e) => setDraft((d) => ({ ...d, industry: e.target.value }))}
                    style={{ marginTop: 4, width: "100%" }}
                  >
                    <option value="">— Not set —</option>
                    {INDUSTRY_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: "block", fontSize: 12, color: "var(--text-2)" }}>
                  Lifetime invoiced (USD, optional)
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    value={draft.lifetimeRevenue}
                    onChange={(e) => setDraft((d) => ({ ...d, lifetimeRevenue: e.target.value }))}
                    style={{ marginTop: 4, width: "100%" }}
                    placeholder="From your billing system"
                  />
                </label>
                <label style={{ display: "block", fontSize: 12, color: "var(--text-2)" }}>
                  Health score (0–100)
                  <input
                    className="input"
                    type="number"
                    disabled={!draft.healthScoreManual}
                    value={draft.healthScoreEd}
                    onChange={(e) => setDraft((d) => ({ ...d, healthScoreEd: e.target.value }))}
                    style={{ marginTop: 4, width: "100%", opacity: draft.healthScoreManual ? 1 : 0.65 }}
                  />
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={draft.healthScoreManual === true}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        healthScoreManual: e.target.checked,
                        healthScoreEd: e.target.checked ? d.healthScoreEd : String(c.healthScore ?? 50),
                      }))
                    }
                  />
                  Lock health score (manual — otherwise recalculated from activity)
                </label>
                {[
                  ["Legacy MRR field", "mrr", "number"],
                  ["Total scans", "totalScans", "number"],
                ].map(([label, field]) => (
                  <label key={field} style={{ display: "block", fontSize: 12, color: "var(--text-2)" }}>
                    {label}
                    <input
                      className="input"
                      type="number"
                      value={draft[field]}
                      onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                      style={{ marginTop: 4, width: "100%" }}
                    />
                  </label>
                ))}
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={draft.isInternal === true}
                    onChange={(e) => setDraft((d) => ({ ...d, isInternal: e.target.checked }))}
                  />
                  Internal account (exclude from revenue & paid-customer metrics)
                </label>
                <label style={{ display: "block", fontSize: 12, color: "var(--text-2)" }}>
                  Notes (on record)
                  <textarea
                    className="textarea"
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    style={{ marginTop: 4, minHeight: 72 }}
                  />
                </label>
                <div>
                  <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 6 }}>Custom fields (map to services later)</div>
                  {(draft.customFieldRows || []).map((row, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <input
                        className="input"
                        placeholder="Key"
                        value={row.key}
                        onChange={(e) => {
                          const next = [...draft.customFieldRows];
                          next[i] = { ...next[i], key: e.target.value };
                          setDraft((d) => ({ ...d, customFieldRows: next }));
                        }}
                        style={{ flex: 1 }}
                      />
                      <input
                        className="input"
                        placeholder="Value"
                        value={row.value}
                        onChange={(e) => {
                          const next = [...draft.customFieldRows];
                          next[i] = { ...next[i], value: e.target.value };
                          setDraft((d) => ({ ...d, customFieldRows: next }));
                        }}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => {
                          const next = draft.customFieldRows.filter((_, j) => j !== i);
                          setDraft((d) => ({ ...d, customFieldRows: next }));
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        customFieldRows: [...(d.customFieldRows || []), { key: "", value: "" }],
                      }))
                    }
                  >
                    + Add field
                  </button>
                </div>
              </div>
            ) : null}

            {(c.tags || []).length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    color: "var(--text-3)",
                    marginBottom: 8,
                  }}
                >
                  Tags
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {c.tags.map((t) => (
                    <span key={t} className="tag-pill">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  color: "var(--text-3)",
                  marginBottom: 8,
                }}
              >
                Lifecycle stage
              </div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8, lineHeight: 1.45 }}>
                Tap a stage to set it manually. Automated events can still move stages when you sync webhooks.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {LIFECYCLE_STAGES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    disabled={!isNew && stageSaving}
                    onClick={() => pickStage(s.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 12px",
                      borderRadius: 8,
                      border: `0.5px solid ${displayStage === s.id ? s.color + "50" : "var(--border)"}`,
                      background: displayStage === s.id ? s.bg : "transparent",
                      cursor: !isNew && stageSaving ? "wait" : "pointer",
                      transition: "all 0.13s",
                      textAlign: "left",
                      width: "100%",
                      font: "inherit",
                      color: "inherit",
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: displayStage === s.id ? s.color : "var(--text-1)",
                        }}
                      >
                        {s.label}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-3)" }}>{s.description}</div>
                    </div>
                    {displayStage === s.id ? <span style={{ fontSize: 10, color: s.color }}>current</span> : null}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "Scans" && !isNew ? (
          <ContactScansTab contact={c} scans={scans} files={contactFiles} />
        ) : null}

        {activeTab === "Timeline" && (
          <div>
            {wsSummary === undefined ? (
              <div
                style={{
                  marginBottom: 14,
                  padding: "10px 12px",
                  fontSize: 12,
                  color: "var(--text-3)",
                  background: "var(--bg-elevated)",
                  borderRadius: 8,
                  border: "0.5px solid var(--border)",
                }}
              >
                Loading workspace summary…
              </div>
            ) : (
              <div
                style={{
                  marginBottom: 14,
                  padding: "12px 14px",
                  background: "var(--bg-elevated)",
                  borderRadius: 8,
                  border: "0.5px solid var(--border)",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>
                  Workspaces & uploads
                </div>
                {wsSummary.workspaces.length === 0 && wsSummary.totalFileDocs === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.5 }}>
                    No workspace or file rows matched <span className="mono">{productUserId || "—"}</span> (owners,
                    workspace members, and file uploader fields). If the timeline still shows activity, those events may
                    use another user id field — set <strong>Firebase UID</strong> on the contact to match the product.
                  </div>
                ) : null}
                {wsSummary.workspaces.length === 0 && wsSummary.totalFileDocs > 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.5 }}>
                    {wsSummary.totalFileDocs} file record(s) linked to this user
                    {wsSummary.filesWithoutWorkspace
                      ? ` · ${wsSummary.filesWithoutWorkspace} without a workspace id on the file doc`
                      : ""}
                    . Add <code style={{ fontSize: 11 }}>workspaceId</code> (or{" "}
                    <code style={{ fontSize: 11 }}>workspace_id</code>) on files to group counts.
                  </div>
                ) : wsSummary.workspaces.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--text-2)", lineHeight: 1.55 }}>
                    {wsSummary.workspaces.map((w) => (
                      <li key={w.id} style={{ marginBottom: 6 }}>
                        <strong style={{ color: "var(--text-1)", fontWeight: 500 }}>{w.title}</strong>
                        {w.address ? (
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{w.address}</div>
                        ) : null}
                        <span style={{ color: "var(--text-3)" }}> · </span>
                        {w.fileCount} file{w.fileCount === 1 ? "" : "s"}
                        {w.sqft != null && w.sqft > 0 ? (
                          <>
                            <span style={{ color: "var(--text-3)" }}> · </span>
                            {formatSqft(w.sqft)} total
                          </>
                        ) : null}
                        {w.scans?.length ? (
                          <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 11, color: "var(--text-3)" }}>
                            {w.scans.map((s) => (
                              <li key={s.id}>
                                {s.label}: {formatSqft(s.sqft)}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {wsSummary.totalFileDocs > 0 || (wsSummary.totalSqft != null && wsSummary.totalSqft > 0) ? (
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 8, lineHeight: 1.45 }}>
                    {wsSummary.totalFileDocs > 0 ? (
                      <>
                        Totals: {wsSummary.totalFileDocs} file doc(s) in scope
                        {wsSummary.filesWithoutWorkspace > 0
                          ? ` · ${wsSummary.filesWithoutWorkspace} not tied to a workspace id`
                          : ""}
                        . Counts cap at {WORKSPACE_SUMMARY_MAX_FILES_PER_FIELD} per user-field query; very large
                        libraries may show partial totals.
                      </>
                    ) : null}
                    {wsSummary.totalSqft != null && wsSummary.totalSqft > 0 ? (
                      <div style={{ marginTop: wsSummary.totalFileDocs > 0 ? 4 : 0 }}>
                        Combined scan area: {formatSqft(wsSummary.totalSqft)}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
            <div
              style={{
                marginBottom: 16,
                padding: "12px",
                background: "var(--bg-elevated)",
                borderRadius: 8,
                border: "0.5px solid var(--border)",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: "var(--text-1)" }}>
                Log correspondence
              </div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 8, lineHeight: 1.45 }}>
                Record calls, outbound emails, or meetings so they appear in this timeline.
              </div>
              <textarea
                className="textarea"
                placeholder="e.g. Emailed proposal · Called — left VM"
                value={touchNote}
                onChange={(e) => setTouchNote(e.target.value)}
                style={{ marginBottom: 8, minHeight: 64 }}
              />
              <button type="button" className="btn btn-sm btn-primary" disabled={touchSaving} onClick={logTouchpoint}>
                {touchSaving ? "Saving…" : "Add to timeline"}
              </button>
            </div>
            {activity.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-3)" }}>
                No events linked to this user id in Firestore yet.
              </div>
            ) : (
              activity.map((ev) => <ActivityItem key={ev.id} event={ev} />)
            )}
          </div>
        )}

        {activeTab === "Tasks" && (
          <div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, alignItems: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Tasks for {c.name.split(" ")[0]}</div>
              <input
                className="input"
                placeholder="New task…"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTask()}
                style={{ flex: 1, minWidth: 120, marginBottom: 0 }}
              />
              <button type="button" className="btn btn-sm btn-primary" onClick={addTask}>
                + Add
              </button>
            </div>
            {tasks.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-3)" }}>No tasks</div>
            ) : (
              tasks.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "11px 0",
                    borderBottom: "0.5px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: "1.5px solid",
                      borderColor: t.status === "completed" ? "var(--green)" : "var(--border-md)",
                      background: t.status === "completed" ? "var(--green-dim)" : "transparent",
                      flexShrink: 0,
                      marginTop: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      color: "var(--green)",
                    }}
                  >
                    {t.status === "completed" ? "✓" : ""}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        textDecoration: t.status === "completed" ? "line-through" : "none",
                        color: t.status === "completed" ? "var(--text-3)" : "var(--text-1)",
                      }}
                    >
                      {t.title}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <span
                        className={`badge ${t.priority === "urgent" ? "badge-red" : t.priority === "high" ? "badge-amber" : "badge-muted"}`}
                        style={{ fontSize: 10 }}
                      >
                        {t.priority}
                      </span>
                      {t.dueAt ? <span style={{ fontSize: 11, color: "var(--text-3)" }}>Due {fmtDate(t.dueAt)}</span> : null}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "Notes" && (
          <div>
            <textarea
              className="textarea"
              placeholder="Add a note about this contact..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={saveNote}>
              Save note
            </button>
            <div style={{ marginTop: 16 }}>
              {c.notes && (
                <div
                  style={{
                    padding: "12px",
                    background: "var(--bg-elevated)",
                    borderRadius: 9,
                    marginBottom: 10,
                    fontSize: 13,
                    color: "var(--text-2)",
                    lineHeight: 1.6,
                  }}
                >
                  {c.notes}
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 6 }}>From contact record</div>
                </div>
              )}
              {notes.map((n) => (
                <div
                  key={n.id}
                  style={{
                    padding: "12px",
                    background: "var(--bg-elevated)",
                    borderRadius: 9,
                    marginBottom: 8,
                    fontSize: 13,
                    color: "var(--text-2)",
                    lineHeight: 1.6,
                  }}
                >
                  {n.text}
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 6 }}>
                    {n.author} · {timeAgo(n.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "Stripe" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {c.stripeCustomerId ? (
              <>
                {[
                  ["Customer ID", <span className="mono" style={{ color: "var(--purple)" }}>{c.stripeCustomerId}</span>],
                  ["Invoiced total", <InvoicedDisplay contact={c} />],
                  ["Next invoice", "—"],
                  [
                    "Payment fails",
                    c.paymentFailCount > 0 ? (
                      <span className="badge badge-red">{c.paymentFailCount} failures</span>
                    ) : (
                      <span className="badge badge-green">Clean</span>
                    ),
                  ],
                ].map(([k, v]) => (
                  <div key={k} className="detail-row">
                    <div className="detail-key">{k}</div>
                    <div className="detail-val">{v}</div>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-3)" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>$</div>
                <div>No Stripe customer linked</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>You can still create one-off payment links below.</div>
              </div>
            )}

            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "0.5px solid var(--border)",
                background: "var(--bg-elevated)",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Payment link</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 10, lineHeight: 1.45 }}>
                Uses Cloud Function <code style={{ fontSize: 10 }}>stripeCreatePaymentLink</code> (requires CRM sign-in).
                Either enter <strong>amount (USD)</strong> or a Stripe <strong>Price ID</strong> (price_…).
              </div>
              <label style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                Product label
                <input className="input" value={plName} onChange={(e) => setPlName(e.target.value)} style={{ marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                Amount (USD)
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 99"
                  value={plAmount}
                  onChange={(e) => setPlAmount(e.target.value)}
                  style={{ marginTop: 4 }}
                />
              </label>
              <label style={{ fontSize: 11, display: "block", marginBottom: 10 }}>
                Or Stripe Price ID
                <input
                  className="input"
                  placeholder="price_…"
                  value={plPriceId}
                  onChange={(e) => setPlPriceId(e.target.value)}
                  style={{ marginTop: 4 }}
                />
              </label>
              <button type="button" className="btn btn-sm btn-primary" disabled={plBusy} onClick={createPaymentLink}>
                {plBusy ? "Creating…" : "Create payment link"}
              </button>
              {plErr ? (
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--red)" }}>{plErr}</div>
              ) : null}
              {plUrl ? (
                <div style={{ marginTop: 10, fontSize: 12 }}>
                  <a href={plUrl} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", wordBreak: "break-all" }}>
                    {plUrl}
                  </a>
                </div>
              ) : null}
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "0.5px solid var(--border)",
                background: "var(--bg-elevated)",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Refund</div>
              <label style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                Payment Intent or Charge ID
                <input
                  className="input"
                  placeholder="pi_… or ch_…"
                  value={refundPi}
                  onChange={(e) => setRefundPi(e.target.value)}
                  style={{ marginTop: 4 }}
                />
              </label>
              <label style={{ fontSize: 11, display: "block", marginBottom: 10 }}>
                Partial amount (USD, optional)
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={refundAmt}
                  onChange={(e) => setRefundAmt(e.target.value)}
                  style={{ marginTop: 4 }}
                />
              </label>
              <button type="button" className="btn btn-sm" disabled={refundBusy} onClick={runRefund}>
                {refundBusy ? "Processing…" : "Issue refund"}
              </button>
              {refundErr ? (
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--red)" }}>{refundErr}</div>
              ) : null}
            </div>
          </div>
        )}

        {activeTab === "Firebase" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {c.firebaseUid ? (
              <>
                {[
                  ["UID", <span className="mono" style={{ color: "var(--amber)" }}>{c.firebaseUid}</span>],
                  ["Sessions (30d)", c.usageEvents30d ? Math.round(c.usageEvents30d / 15) : "—"],
                  ["Events (30d)", c.usageEvents30d?.toLocaleString() || "—"],
                  ["Plan utilization", c.usagePctOfLimit ? `${c.usagePctOfLimit}%` : "—"],
                  ["Last active", c.lastActiveAt ? timeAgo(c.lastActiveAt) : "—"],
                  ["Total scans", c.totalScans ?? 0],
                  ["Last scan", c.lastScanAt ? timeAgo(c.lastScanAt) : "—"],
                  ...(deviceDetails.length > 0
                    ? deviceDetails.map((row) => [row.label, row.value])
                    : [["Device", "—"]]),
                ].map(([k, v]) => (
                  <div key={k} className="detail-row">
                    <div className="detail-key">{k}</div>
                    <div className="detail-val">{v}</div>
                  </div>
                ))}
                {c.usagePctOfLimit > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: "var(--text-3)" }}>Plan utilization</span>
                      <span>{c.usagePctOfLimit}%</span>
                    </div>
                    <ProgressBar value={c.usagePctOfLimit} color={c.usagePctOfLimit >= 80 ? "var(--amber)" : "var(--accent)"} />
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-3)" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>◉</div>
                <div>No Firebase account linked</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Will auto-link on first app login</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
